import TimerDisciplineMark from "@/components/discipline/TimerDisciplineMark";
import type {CampaignManagementInfluencerSerializer} from "@/generated/Influencer";
import type {PunishmentSerializer} from "@/generated/Punishment";
import {useDiscipline} from "@/hooks/useDiscipline";
import {useIsAdmin} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import {disciplineTabAtom, isDisciplinaryModalOpenAtom} from "@/store/jotaiAtoms";
import type {TDisciplineCampaign, TInfluencerProfileDetail} from "@/store/types";
import {isUnverifiedType} from "@/utils/disciplineUtils";
import {useSetAtom} from "jotai";
import {isEmpty} from "lodash";
import {memo, useCallback, useMemo, type FC} from "react";
import {useTranslation} from "react-i18next";

const DisciplineIndicators: FC<{
  campaignApplication?: TDisciplineCampaign;
  className?: string;
  disabled?: boolean;
  influencer: Partial<TInfluencerProfileDetail | CampaignManagementInfluencerSerializer>;
  showEmptyState?: boolean;
  showMostImpactfulOnly?: boolean;
  showUnverifications?: boolean;
}> = ({
  campaignApplication,
  className,
  disabled,
  influencer,
  showEmptyState,
  showMostImpactfulOnly,
  showUnverifications,
}) => {
  const setDisciplinaryModalOpen = useSetAtom(isDisciplinaryModalOpenAtom);
  const setSelectedDisciplineTab = useSetAtom(disciplineTabAtom);
  const isAdmin = useIsAdmin();
  const {initializeDisciplineModal} = useDiscipline();
  const {t} = useTranslation("influencer_discipline");

  const unverifications = useMemo(
    () =>
      [
        influencer.user?.instagram_unverified,
        influencer.user?.tiktok_unverified,
        influencer.user?.ugc_unverified,
      ].filter(unverification => !!unverification),
    [influencer.user?.instagram_unverified, influencer.user?.tiktok_unverified, influencer.user?.ugc_unverified],
  );

  const onClickIndicator = useCallback(
    (punishment?: PunishmentSerializer | null) => {
      initializeDisciplineModal({
        campaignApplication,
        influencer: influencer as TInfluencerProfileDetail,
        selectedPunishment: punishment,
      });

      setSelectedDisciplineTab(
        !punishment || isUnverifiedType(punishment?.punishment_type) ? "UNVERIFY" : punishment?.punishment_type,
      );
      setDisciplinaryModalOpen(true);
    },
    [influencer, setDisciplinaryModalOpen, setSelectedDisciplineTab],
  );

  if (!isAdmin) {
    return null;
  }

  if (showEmptyState && !influencer.user?.last_punishment && isEmpty(unverifications)) {
    return <span className="text-neutral-black font-semibold">{t("no_status")}</span>;
  }

  return (
    <div className={clsxm("flex gap-2", className)}>
      {influencer.user?.last_punishment && (
        <TimerDisciplineMark
          discipline={influencer.user.last_punishment}
          onClick={disabled ? undefined : () => onClickIndicator(influencer.user?.last_punishment)}
          type={influencer.user.last_punishment.punishment_type}
        />
      )}
      {(!showMostImpactfulOnly || !influencer.user?.last_punishment) && showUnverifications && (
        <>
          {unverifications.length === 1 && (
            <TimerDisciplineMark
              discipline={unverifications[0]}
              onClick={disabled ? undefined : () => onClickIndicator(unverifications[0])}
              type={unverifications[0].punishment_type}
            />
          )}
          {unverifications.length > 1 && (
            <TimerDisciplineMark
              instagramUnverificationId={influencer.user?.instagram_unverified?.id}
              onClick={disabled ? undefined : () => onClickIndicator()}
              tiktokUnverificationId={influencer.user?.tiktok_unverified?.id}
              type="GENERAL_UNVERIFIED"
              userId={influencer.user?.id}
              ugcUnverificationId={influencer.user?.ugc_unverified?.id}
            />
          )}
        </>
      )}
    </div>
  );
};

export default memo(DisciplineIndicators);
